import React, { createContext, useContext, useState } from 'react';

const TuitionContext = createContext();

export const useTuitionContext = () => {
    const context = useContext(TuitionContext);
    if (!context) {
        throw new Error('useTuitionContext must be used within a TuitionProvider');
    }
    return context;
};

export const TuitionProvider = ({ children }) => {
    // State variables
    const [currentTuition, setCurrentTuition] = useState('');
    const [studentCount, setStudentCount] = useState('');
    const [profitPercentage, setProfitPercentage] = useState(50);
    const [showPanel, setShowPanel] = useState(true);
    const [showInstructions, setShowInstructions] = useState(true);
    const [hasCalculated, setHasCalculated] = useState(false);

    const [expenses, setExpenses] = useState([
        { label: 'Lease Payment', value: '', id: 1 },
        { label: 'Monthly Payroll', value: '', id: 2 },
        { label: 'Royalty Payment', value: '', id: 3 },
        { label: 'Tech Fee', value: '', id: 4 },
        { label: 'Advertising', value: '', id: 5 },
        { label: 'Office Expense', value: '', id: 6 },
        { label: 'Student Database', value: '', id: 7 },
        { label: 'Telephone', value: '', id: 8 },
        { label: 'Internet', value: '', id: 9 },
        { label: 'Insurance', value: '', id: 10 },
        { label: 'Loan Payment', value: '', id: 11 },
        { label: 'Other Utilities', value: '', id: 12 }
    ]);

    const [customExpenses, setCustomExpenses] = useState([]);
    const [newExpenseLabel, setNewExpenseLabel] = useState('');

    // Calculation methods
    const calculateTotalExpenses = () => {
        const standardExpenses = expenses.reduce((sum, expense) =>
            sum + (parseFloat(expense.value) || 0), 0);
        const customExpensesTotal = customExpenses.reduce((sum, expense) =>
            sum + (parseFloat(expense.value) || 0), 0);
        return standardExpenses + customExpensesTotal;
    };


    const calculateOptimumTuition = (totalExpenses, students, profitMargin) => {
        // Formula: (Total Expenses / (1 - Profit Margin)) / Number of Students
        // This ensures profit margin is calculated on revenue, not expenses
        const requiredRevenue = totalExpenses / (1 - profitMargin / 100);
        return requiredRevenue / students;
    };

    const calculateCurrentRevenue = () => {
        return (parseFloat(currentTuition) || 0) * studentCount;
    };

    const calculateCurrentProfit = () => {
        const revenue = calculateCurrentRevenue();
        const expenses = calculateTotalExpenses();
        return revenue - expenses;
    };

    const calculateCurrentProfitMargin = () => {
        const revenue = calculateCurrentRevenue();
        const profit = calculateCurrentProfit();
        return revenue > 0 ? (profit / revenue) * 100 : 0;
    };

    // Utility methods
    const formatCurrency = (amount, includeDecimal) => {
        if (isNaN(amount) || amount === null || amount === undefined) {
            return '$';
        }

        var fmtAmt = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(amount);

        return includeDecimal ? fmtAmt : fmtAmt.split('.')[0];
    };

    const addCustomExpense = () => {
        if (newExpenseLabel.trim()) {
            const newExpense = {
                label: newExpenseLabel.trim(),
                value: '',
                id: Date.now()
            };
            setCustomExpenses([...customExpenses, newExpense]);
            setNewExpenseLabel('');
        }
    };

    const removeCustomExpense = (id) => {
        setCustomExpenses(customExpenses.filter(expense => expense.id !== id));
    };

    const updateExpense = (id, value) => {
        setExpenses(expenses.map(expense =>
            expense.id === id ? { ...expense, value } : expense
        ));
    };

    const updateCustomExpense = (id, value) => {
        setCustomExpenses(customExpenses.map(expense =>
            expense.id === id ? { ...expense, value } : expense
        ));
    };

    const validateAndCalculate = () => {
        const totalExpenses = calculateTotalExpenses();
        if (!currentTuition || studentCount <= 0 || totalExpenses <= 0) {
            alert('Please fill in all required fields with valid values.');
            return;
        }
        setShowPanel(true);
        setShowInstructions(false);
        setHasCalculated(true)
    };

    const getSpeedometerColor = () => {
        // Return color based on profit margin percentage
        const currentProfit = calculateCurrentProfitMargin();
        if (currentProfit < 20) return '#EA4228'; 
        if (currentProfit < 40) return '#F58B19'; 
        if (currentProfit < 50) return '#F5CD19'; 
        if (currentProfit < 60) return '#5BE12C'; 
        return '#017000'; 
    };

    const getGaugeRotation = (current, optimum) => {
        const max = Math.max(current, optimum) * 1.2;
        const currentAngle = (current / max) * 180 - 90;
        const optimumAngle = (optimum / max) * 180 - 90;
        return { currentAngle, optimumAngle, max };
    };

    const exportToCSV = () => {
        const totalExpenses = calculateTotalExpenses();
        const optimumTuition = calculateOptimumTuition(totalExpenses, studentCount, profitPercentage);
        const currentTuitionNum = parseFloat(currentTuition) || 0;

        const data = [
            ['Current Enrollment', studentCount],
            ['Current Tuition', formatCurrency(currentTuitionNum)],
            ['Optimum Tuition', formatCurrency(optimumTuition)],
            ['Profit Percentage', `${profitPercentage}%`],
            ['Total Expenses', formatCurrency(totalExpenses)],
            ['Current Revenue', formatCurrency(calculateCurrentRevenue())],
            ['Current Profit', formatCurrency(calculateCurrentProfit())],
            ['Current Profit Margin', `${calculateCurrentProfitMargin().toFixed(2)}%`],
            ['', ''],
            ['Expense Breakdown', ''],
            ...expenses.filter(e => e.value).map(e => [e.label, formatCurrency(parseFloat(e.value))]),
            ...customExpenses.map(e => [e.label, formatCurrency(parseFloat(e.value))])
        ];

        const csv = data.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tuition-calculation.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    const printPDF = () => {
        window.print();
    };

    // Computed values
    const totalExpenses = calculateTotalExpenses();
    const optimumTuition = calculateOptimumTuition(totalExpenses, studentCount, profitPercentage);
    const currentTuitionNum = parseFloat(currentTuition) || 0;
    const allExpenses = [...expenses.filter(e => e.value), ...customExpenses];
    const expenseData = allExpenses.map(expense => ({
        label: expense.label,
        value: parseFloat(expense.value) || 0
    }));

    const value = {
        // State
        currentTuition,
        setCurrentTuition,
        studentCount,
        setStudentCount,
        profitPercentage,
        setProfitPercentage,
        showPanel,
        setShowPanel,
        expenses,
        setExpenses,
        customExpenses,
        setCustomExpenses,
        newExpenseLabel,
        setNewExpenseLabel,
        showInstructions,
        setShowInstructions,
        hasCalculated,

        // Calculation methods
        calculateTotalExpenses,
        calculateOptimumTuition,
        calculateCurrentRevenue,
        calculateCurrentProfit,
        calculateCurrentProfitMargin,

        // Utility methods
        formatCurrency,
        addCustomExpense,
        removeCustomExpense,
        updateExpense,
        updateCustomExpense,
        validateAndCalculate,
        getSpeedometerColor,
        getGaugeRotation,
        exportToCSV,
        printPDF,

        // Computed values
        totalExpenses,
        optimumTuition,
        currentTuitionNum,
        allExpenses,
        expenseData
    };

    return (
        <TuitionContext.Provider value={value}>
            {children}
        </TuitionContext.Provider>
    );
};
